<script setup>
import { computed, onBeforeMount, onMounted, watchEffect } from "vue";
import { TuBasePopup, TuButton, ButtonSeverity } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import { useRoute } from "vue-router/composables";
import { FocusTrap } from "focus-trap-vue";
import { RouterLink } from "vue-router";

import useTranslation from "@Composables/useTranslation";
import { useCookieConsent } from "@Store/overhauling/useCookieConsent";

const route = useRoute();
const { translate } = useTranslation();
const cookieStore = useCookieConsent();

const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

async function onAllowRequiredConsent() {
  await cookieStore.xRecordRequiredConsent();
}

async function onAllowAllConsent() {
  await cookieStore.xRecordAllConsent();
}

function onClose() {
  cookieStore.xUserSkippedCookieConsent();
}

onMounted(() => {
  cookieStore.xFetchConsentChoices();
  if (isOverlayEnable.value) {
    document.body.style.overflow = "hidden";
    return;
  }
});

onBeforeMount(() => { document.body.style.overflow = ""; });

const isVisible = computed(() => !cookieStore.hasConsented && !cookieStore.hasSkipped);
const isGdprRequired = computed(() => cookieStore.consentRequired && (cookieStore.consentType === "gdpr"));
const isClosable = computed(() => !isGdprRequired.value);
const isOverlayEnable = computed(() => {
  const legalPages = [
    "privacy-manager",
    "accessibility",
    "cookies",
    "privacy",
    "terms",
    "dpa",
  ];

  const currentLocation = route?.name;

  return isGdprRequired.value
    && isVisible.value
    && !legalPages.includes(currentLocation);
});

watchEffect(() => {
  if (isOverlayEnable.value) {
    document.body.style.overflow = "hidden";

    return;
  }

  document.body.style.overflow = "";
});
</script>

<template>
  <div
    v-if="isVisible"
    class="cookie-consent-wrapper _cookie-consent"
    :class="{
      'cookie-consent-wrapper--overlay': isOverlayEnable,
      'cookie-consent-wrapper--mobile': isMobileView,
    }"
  >
    <FocusTrap
      :active="isOverlayEnable"
      :escape-deactivates="false"
      :return-focus-on-deactivate="false"
      :click-outside-deactivates="false"
    >
      <TuBasePopup
        id="cookie-consent"
        tabindex="-1"
        class="cookie-consent"
        :class="{
          'cookie-consent--mobile': isMobileView,
          'cookie-consent--overlay': isOverlayEnable,
        }"
        is-borderless
        :is-closeable="isClosable"
        @close="onClose"
      >
        <template #title>
          <span>{{ translate("cookie_consent.title") }}</span>
        </template>
        <template #content>
          <p class="cookie-consent__content _cookie-consent-content">
            {{ translate("cookie_consent.content") }}
            <RouterLink
              class="cookie-consent__link _cookie-consent-link"
              :to="{ name: 'cookies' }"
            >
              {{ translate("cookie_consent.link_content") }}
            </RouterLink>
          </p>
        </template>
        <template #footer>
          <div class="cookie-consent__footer">
            <TuButton
              class="cookie-consent__footer-button"
              data-test-id="popup-footer-button-allow-only-necessary"
              is-outlined
              :label="translate('cookie_consent.allow_optional')"
              :severity="ButtonSeverity.CANCEL"
              @click="onAllowRequiredConsent"
            />
            <TuButton
              class="cookie-consent__footer-button"
              data-test-id="popup-footer-button-allow-all"
              :label="translate('cookie_consent.allow_require')"
              @click="onAllowAllConsent"
            />
          </div>
        </template>
      </TuBasePopup>
    </FocusTrap>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.cookie-consent-wrapper {
  position: fixed;
  right: 0px;
  bottom: 0px;
  z-index: 100;

  &--overlay {
    display: flex;
    flex-flow: column;
    justify-content: flex-end;
    width: 100vw;
    height: 100vh;

    background-color: color.$main-overlay;
  }

  &--mobile {
    width: 100%;
  }
}

.cookie-consent {
  $self: &;

  margin: spacing.$xxs;

  &__content {
    margin-bottom: 0px;

    font-size: typography.$s-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$s-line-height;
  }

  &__link {
    color: color.$primary;
  }

  &__footer {
    display: flex;
    gap: spacing.$xxs;
    justify-content: flex-end;
  }

  &--mobile {
    margin: 0;

    border-radius: border.$medium-radius border.$medium-radius 0 0;

    #{ $self }__footer {
      flex-flow: column;
      justify-content: center;
    }
  }

  &--overlay {
    margin-left: auto;
  }
}
</style>
